// src/services/api.ts — Typed API service with standardized error handling
import type {
  AuthResponse,
  User,
  Team,
  TeamMember,
  Region,
  Project,
  CreateProjectData,
  Service,
  CreateServiceData,
  MLService,
  MLModel,
  Volume,
  DeployMLModelData,
  RegisterModelData,
  GitHubRepo,
  GitHubBranch,
  NotificationSetting,
  EnvGroup,
  Session
} from '../types';

import { apiClient, API_URL, ApiError } from './apiClient';
import { agentApi } from './agentApi';
import { billingApi } from './billingApi';
import { deploymentApi } from './deploymentApi';
import { aiAgentsApi } from './aiAgentsApi';
import { prebuiltAgentApi } from './prebuiltAgentApi';

export { ApiError };

const api = {
  ...apiClient,
  getBlob: (path: string) => apiClient.getBlob(path),

  // ============================================
  // AUTH ENDPOINTS
  // ============================================

  register: async (email: string, password: string): Promise<AuthResponse> => {
    const res = await fetch(`${API_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    const data: AuthResponse = await res.json();
    if (!res.ok && !data.error) {
      throw new ApiError(res.status, 'Registration failed');
    }
    return data;
  },

  login: async (email: string, password: string): Promise<AuthResponse> => {
    const res = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    const data: AuthResponse = await res.json();
    if (!res.ok && !data.error) {
      throw new ApiError(res.status, 'Login failed');
    }
    return data;
  },

  getMe: (): Promise<{ user: User }> => apiClient.get('/auth/me'),

  forgotPassword: async (email: string): Promise<{ success: boolean; message: string }> => {
    const res = await fetch(`${API_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    return res.json();
  },

  resetPassword: async (token: string, password: string): Promise<{ success: boolean; message: string }> => {
    const res = await fetch(`${API_URL}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password })
    });
    return res.json();
  },

  verifyEmail: async (token: string): Promise<{ success: boolean; message: string; email?: string }> => {
    const res = await fetch(`${API_URL}/auth/verify-email?token=${encodeURIComponent(token)}`);
    return res.json();
  },

  // ============================================
  // SESSION MANAGEMENT
  // ============================================

  getUserSessions: (): Promise<{ sessions: Session[] }> => apiClient.get('/auth/sessions'),
  revokeSession: (sessionId: number): Promise<{ success: boolean; message: string }> => 
    apiClient.del(`/auth/sessions/${sessionId}`),
  revokeAllSessions: (): Promise<{ success: boolean; message: string; revokedSessions: number }> => 
    apiClient.post('/auth/logout-all', {}),

  // ============================================
  // TWO-FACTOR AUTHENTICATION (2FA)
  // ============================================

  verify2FALogin: async (tempToken: string, token: string): Promise<AuthResponse> => {
    const res = await fetch(`${API_URL}/auth/login/verify-2fa`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tempToken, token })
    });
    const data: AuthResponse = await res.json();
    if (!res.ok && !data.error) {
      throw new ApiError(res.status, 'Invalid 2FA code');
    }
    return data;
  },

  setup2FA: (): Promise<{ secret: string; qrCode: string }> => apiClient.post('/auth/2fa/setup'),
  verify2FA: (token: string): Promise<{ success: boolean; message: string; backupCodes: string[] }> => apiClient.post('/auth/2fa/verify', { token }),
  disable2FA: (): Promise<{ success: boolean; message: string }> => apiClient.post('/auth/2fa/disable'),

  // ============================================
  // GITHUB
  // ============================================

  disconnectGithub: (): Promise<{ message: string }> => apiClient.post('/auth/github/disconnect', {}),
  getGithubRepos: (): Promise<GitHubRepo[]> => apiClient.get('/github/repos'),
  getRepoBranches: (owner: string, repo: string): Promise<GitHubBranch[]> =>
    apiClient.get(`/github/repos/${owner}/${repo}/branches`),

  // OAuth redirects (no return value)
  loginWithGoogle: (): void => {
    window.location.href = `${API_URL}/auth/google`;
  },

  connectGoogle: (): void => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No auth token found. Please login first.');
      return;
    }
    window.location.href = `${API_URL}/auth/google/connect?token=${token}`;
  },

  loginWithGithub: (): void => {
    window.location.href = `${API_URL}/auth/github/login`;
  },

  connectGithub: (): void => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No auth token found. Please login first.');
      return;
    }
    window.location.href = `${API_URL}/auth/github?token=${token}`;
  },

  handleOAuthCallback: (token: string): boolean => {
    if (token) {
      apiClient.setToken(token);
      return true;
    }
    return false;
  },

  // ============================================
  // TEAMS
  // ============================================

  getTeams: (): Promise<{ teams: Team[] }> => apiClient.get('/teams'),
  createTeam: (name: string, slug: string): Promise<{ team: Team, message: string }> => apiClient.post('/teams', { name, slug }),
  getTeamMembers: (teamId: number): Promise<{ members: TeamMember[] }> => apiClient.get(`/teams/${teamId}/members`),
  inviteUser: (teamId: number, email: string, role: string): Promise<{ message: string, token: string }> => apiClient.post(`/teams/${teamId}/invites`, { email, role }),
  updateTeamMemberRole: (teamId: number, userId: number, role: TeamMember['role']): Promise<{ member: TeamMember; message: string }> =>
    apiClient.patch(`/teams/${teamId}/members/${userId}`, { role }),
  removeTeamMember: (teamId: number, userId: number): Promise<{ message: string }> =>
    apiClient.del(`/teams/${teamId}/members/${userId}`),
  transferTeamOwnership: (teamId: number, newOwnerUserId: number): Promise<{ message: string; newOwnerUserId: number }> =>
    apiClient.post(`/teams/${teamId}/transfer-owner`, { newOwnerUserId }),
  acceptInvite: (token: string): Promise<{ message: string }> => apiClient.post('/teams/invites/accept', { token }),

  // ============================================
  // REGIONS
  // ============================================

  getRegions: (): Promise<{ regions: Region[] }> => apiClient.get('/regions'),

  // ============================================
  // API KEYS & RATE LIMITS
  // ============================================

  getApiKeys: (): Promise<any> => apiClient.get('/api-keys'),
  createApiKey: (name: string, expiresInDays?: number, permissions?: string[]): Promise<any> => 
    apiClient.post('/api-keys', { name, expiresInDays, permissions }),
  revokeApiKey: (id: number): Promise<any> => apiClient.del(`/api-keys/${id}`),
  rotateApiKey: (id: number): Promise<{ newKey: string }> => apiClient.post(`/api-keys/${id}/rotate`, {}),
  updateApiKeyPermissions: (id: number, permissions: string[]): Promise<void> => 
    apiClient.put(`/api-keys/${id}/permissions`, { permissions }),
  getApiKeyAuditLog: (id: number): Promise<{ auditLog: any[] }> => apiClient.get(`/api-keys/${id}/audit`),

  // ============================================
  // PROJECTS
  // ============================================

  getProjects: (teamId?: number | null): Promise<Project[]> => apiClient.get(`/projects${teamId ? `?teamId=${teamId}` : ''}`),
  createProject: (data: CreateProjectData): Promise<Project> => apiClient.post('/projects', data),
  getProject: (id: string | number): Promise<Project> => apiClient.get(`/projects/${id}`),
  deleteProject: (id: string | number): Promise<{ message: string }> => apiClient.del(`/projects/${id}`),
  scanProject: (
    id: string | number,
    options: { rootDirectory?: string; serviceType?: 'web' | 'static'; outputDirectory?: string } = {}
  ): Promise<{
    detected: boolean;
    framework: string;
    recommendedServiceType: string;
    recommendedRuntime: string;
    expectedOutputDirectory?: string | null;
    rootDirectory: string;
    reasons: string[];
    compatibility: { compatible: boolean | null; blocking: boolean; message: string };
  }> => {
    const params = new URLSearchParams();
    if (options.rootDirectory) params.set('rootDirectory', options.rootDirectory);
    if (options.serviceType) params.set('serviceType', options.serviceType);
    if (options.outputDirectory) params.set('outputDirectory', options.outputDirectory);
    const query = params.toString();
    return apiClient.get(`/projects/${id}/scan${query ? `?${query}` : ''}`);
  },

  // ============================================
  // SERVICES
  // ============================================

  getServices: (teamId?: number | null): Promise<Service[]> => apiClient.get(`/services${teamId ? `?teamId=${teamId}` : ''}`),
  createService: (data: CreateServiceData): Promise<Service> => apiClient.post('/services', data),
  updateService: (id: string | number, data: any): Promise<{ success: boolean; service: Service; message: string }> =>
    apiClient.put(`/services/${id}`, data),
  deleteService: (id: string | number): Promise<{ message: string }> => apiClient.del(`/services/${id}`),
  updateServiceEnv: (serviceId: string | number, envVars: Record<string, string>): Promise<Service> =>
    apiClient.put(`/services/${serviceId}/env`, { envVars }),
  // Cron runs (Phase 3C)
  getCronRuns: (serviceId: string | number, limit: number = 20): Promise<any> =>
    apiClient.get(`/services/${serviceId}/runs?limit=${limit}`),
  getCronRunLogs: (serviceId: string | number, runId: string, lines: number = 500): Promise<any> =>
    apiClient.get(`/services/${serviceId}/runs/${encodeURIComponent(runId)}/logs?lines=${lines}`),
  runCronNow: (serviceId: string | number): Promise<any> => apiClient.post(`/services/${serviceId}/runs`, {}),
  // Secure configuration (Phase 3A). No call returns a secret value.
  getServiceConfiguration: (serviceId: string | number): Promise<any> =>
    apiClient.get(`/services/${serviceId}/config`),
  setServiceSecret: (serviceId: string | number, name: string, value: string): Promise<any> =>
    apiClient.put(`/services/${serviceId}/secrets/${encodeURIComponent(name)}`, { value }),
  convertServiceVariableToSecret: (serviceId: string | number, name: string): Promise<any> =>
    apiClient.post(`/services/${serviceId}/secrets/${encodeURIComponent(name)}/convert`, {}),
  deleteServiceSecret: (serviceId: string | number, name: string): Promise<any> =>
    apiClient.del(`/services/${serviceId}/secrets/${encodeURIComponent(name)}`),
  removeServiceConfigRequirement: (serviceId: string | number, name: string): Promise<any> =>
    apiClient.del(`/services/${serviceId}/config/requirements/${encodeURIComponent(name)}`),
  // Pricing & Usage
  getPricingPlans: (): Promise<any> => apiClient.get('/pricing/plans'),
  getPlatformStatus: (): Promise<any> => apiClient.get('/status'),
  getPaidPlanStatus: (): Promise<any> => apiClient.get('/payment/plans/status'),
  createStarterPlanOrder: (data: { returnPath?: string; attribution?: Record<string, string> }): Promise<any> =>
    apiClient.post('/payment/plans/starter/create-order', data),
  purchaseStarterWithWallet: (idempotencyKey: string): Promise<any> =>
    apiClient.post('/payment/plans/starter/wallet-purchase', { idempotencyKey }),
  setStarterAutoRenew: (enabled: boolean): Promise<any> =>
    apiClient.post('/payment/plans/starter/auto-renew', { enabled }),
  changeServicePlan: (serviceId: string | number, plan: string, billingType: string): Promise<any> => 
    apiClient.put(`/services/${serviceId}/plan`, { plan, billingType }),
  checkServiceHealth: (serviceId: string | number): Promise<{ status?: string; health?: { status: string } }> =>
    apiClient.get(`/services/${serviceId}/health`),
  stopService: (serviceId: string | number): Promise<{ message: string }> =>
    apiClient.post(`/services/${serviceId}/stop`, {}),
  restartService: (serviceId: string | number): Promise<{ message: string }> =>
    apiClient.post(`/services/${serviceId}/restart`, {}),
  resumeService: (serviceId: string | number): Promise<{ message: string }> =>
    apiClient.post(`/services/${serviceId}/resume`, {}),
  updateServiceSchedule: (serviceId: string | number, cronSchedule: string, cronTimezone?: string): Promise<{ message: string; service: Service }> =>
    apiClient.put(`/services/${serviceId}/schedule`, { cronSchedule, cronTimezone }),
  getServiceRuntimeLogs: (serviceId: string | number, lines: number = 100): Promise<{ logs: string[] }> =>
    apiClient.get(`/services/${serviceId}/logs?lines=${lines}`),
  getAnalyticsSummary: (): Promise<any> =>
    apiClient.get('/services/analytics/summary'),
  getDockerCompose: (serviceId: string | number): Promise<{ success: boolean; dockerCompose: string }> =>
    apiClient.get(`/services/${serviceId}/docker-compose`),
  getContainerStats: (serviceId: string | number): Promise<any> =>
    apiClient.get(`/services/${serviceId}/stats`),
  updateServiceCdn: (serviceId: string | number, enableCdn: boolean): Promise<{ service: Service; message: string }> =>
    apiClient.put(`/services/${serviceId}/cdn`, { enableCdn }),

  ...deploymentApi,
  ...aiAgentsApi,
  ...billingApi,

  // ============================================
  // VOLUMES
  // ============================================

  getVolumes: (projectId: number): Promise<{ volumes: Volume[] }> => apiClient.get(`/volumes/projects/${projectId}`),
  getWorkspaceVolumes: (teamId?: number | null): Promise<{ volumes: Volume[] }> =>
    apiClient.get(`/volumes${teamId ? `?teamId=${teamId}` : ''}`),
  createVolume: (data: { projectId: number; name: string; size_gb: number }): Promise<{ message: string, volume: Volume }> => apiClient.post('/volumes', data),
  updateVolume: (volumeId: number, data: Partial<Volume>): Promise<{ message: string, volume: Volume }> => apiClient.put(`/volumes/${volumeId}`, data),
  attachVolume: (volumeId: number, data: { serviceId: number; mountPath: string }): Promise<{ message: string; volume?: Volume; deployment?: unknown }> => apiClient.post(`/volumes/${volumeId}/attach`, data),
  detachVolume: (volumeId: number): Promise<{ message: string; volume?: Volume; deployment?: unknown }> => apiClient.post(`/volumes/${volumeId}/detach`),
  deleteVolume: (volumeId: number): Promise<{ message: string }> => apiClient.del(`/volumes/${volumeId}`),
  // Phase 3F: a whole project — apps, links, config status, domains, volumes, health, costs, problems.
  getProjectTopology: (projectId: string | number): Promise<{ topology: any }> => apiClient.get(`/projects/${projectId}/topology`),
  // The project's default branch; applyToApps also switches every git app in it (from the next deploy).
  updateProjectBranch: (projectId: string | number, branch: string, applyToApps: boolean): Promise<{ project: any; previousBranch: string | null; appsUpdated: number; message: string }> =>
    apiClient.put(`/projects/${projectId}/branch`, { branch, applyToApps }),
  // Phase 3E: one volume's usage, price, charges and backups; backups and restore.
  getVolume: (volumeId: number): Promise<{ volume: any; backups: any }> => apiClient.get(`/volumes/${volumeId}`),
  createBackup: (resourceType: 'redis' | 'volume', resourceId: number): Promise<{ backup: any }> =>
    apiClient.post('/backups', { resource_type: resourceType, resource_id: resourceId }),
  restoreBackup: (backupId: string, confirmName: string): Promise<{ restored: boolean; message: string }> =>
    apiClient.post(`/backups/${backupId}/restore`, { confirm_name: confirmName }),
  // Phase 3G: infrastructure plans. Approval here is the user's own click (D-11).
  getPlan: (planId: string): Promise<{ plan: any }> => apiClient.get(`/plans/${planId}`),
  approvePlan: (planId: string): Promise<{ plan: any }> => apiClient.post(`/plans/${planId}/approve`, {}),
  applyPlan: (planId: string): Promise<{ plan: any }> => apiClient.post(`/plans/${planId}/apply`, {}),
  cancelPlan: (planId: string): Promise<{ plan: any }> => apiClient.post(`/plans/${planId}/cancel`, {}),
  cleanupPlan: (planId: string): Promise<{ plan: any }> => apiClient.post(`/plans/${planId}/cleanup`, {}),
  changePlanBilling: (planId: string, billing: 'monthly' | 'hourly'): Promise<{ plan: any }> =>
    apiClient.post(`/plans/${planId}/billing`, { billing }),
  // Phase 4A: Vitibase databases, managed by Replov (D-08).
  getVitibaseStatus: (projectId: string | number): Promise<{ available: boolean; account: any; canManage: boolean }> =>
    apiClient.get(`/integrations/vitibase/status?projectId=${projectId}`),
  openVitibaseStudio: (projectId: string | number): Promise<{ url: string; expiresInSeconds: number }> =>
    apiClient.post('/integrations/vitibase/studio', { projectId }),
  // Phase 4C: the project's databases (names, status, price; never a login).
  listVitibaseDatabases: (projectId: string | number): Promise<{ databases: any[] }> =>
    apiClient.get(`/integrations/vitibase/databases?projectId=${projectId}`),
  detachVitibaseDatabase: (databaseId: string, bindingId: string): Promise<{ message: string }> =>
    apiClient.del(`/integrations/vitibase/databases/${databaseId}/bindings/${bindingId}`),
  deleteVitibaseDatabase: (databaseId: string, confirmName: string): Promise<{ message: string }> =>
    apiClient.del(`/integrations/vitibase/databases/${databaseId}`, { confirm_name: confirmName }),

  // ============================================
  // NOTIFICATIONS
  // ============================================

  getProjectNotifications: (projectId: number): Promise<{ notifications: NotificationSetting[] }> => apiClient.get(`/notifications/project/${projectId}`),
  createProjectNotification: (projectId: number, data: Partial<NotificationSetting>): Promise<{ message: string, notification: NotificationSetting }> => apiClient.post(`/notifications/project/${projectId}`, data),
  updateNotification: (notificationId: number, data: Partial<NotificationSetting>): Promise<{ message: string, notification: NotificationSetting }> => apiClient.put(`/notifications/${notificationId}`, data),
  deleteNotification: (notificationId: number): Promise<{ message: string }> => apiClient.del(`/notifications/${notificationId}`),
  testNotification: (notificationId: number): Promise<{ message: string }> => apiClient.post(`/notifications/${notificationId}/test`),

  // ============================================
  // ENV GROUPS
  // ============================================

  getEnvGroups: (teamId: number): Promise<{ groups: EnvGroup[] }> => apiClient.get(`/env-groups/${teamId}`),
  createEnvGroup: (data: Partial<EnvGroup>): Promise<{ message: string, group: EnvGroup }> => apiClient.post(`/env-groups`, data),
  updateEnvGroup: (groupId: number, data: Partial<EnvGroup>): Promise<{ message: string, group: EnvGroup }> => apiClient.put(`/env-groups/${groupId}`, data),
  deleteEnvGroup: (groupId: number): Promise<{ message: string }> => apiClient.del(`/env-groups/${groupId}`),
  linkEnvGroupToService: (groupId: number, serviceId: string | number): Promise<{ message: string }> => apiClient.post(`/env-groups/${groupId}/services/${serviceId}`),
  unlinkEnvGroupFromService: (groupId: number, serviceId: string | number): Promise<{ message: string }> => apiClient.del(`/env-groups/${groupId}/services/${serviceId}`),

  // ============================================
  // MLOPS
  // ============================================

  getMLServices: (teamId?: number | null): Promise<{ success: boolean; mlServices: MLService[] }> =>
    apiClient.get(`/mlops/services${teamId ? `?teamId=${teamId}` : ''}`),
  deployMLModel: (data: DeployMLModelData): Promise<{ success: boolean; serviceId: number }> =>
    apiClient.post('/mlops/deploy', data),
  getMLModels: (projectId: number | null = null): Promise<{ success: boolean; models: MLModel[] }> => {
    const query = projectId ? `?projectId=${projectId}` : '';
    return apiClient.get(`/mlops/models${query}`);
  },
  stopMLService: (serviceId: number): Promise<{ success: boolean; message: string }> =>
    apiClient.post(`/mlops/services/${serviceId}/stop`, {}),
  deleteMLService: (serviceId: number): Promise<{ success: boolean }> =>
    apiClient.del(`/mlops/services/${serviceId}`),
  checkMLServiceHealth: (serviceId: number): Promise<{ success: boolean; metrics?: unknown }> =>
    apiClient.get(`/mlops/metrics/${serviceId}`),
  getMLServiceLogs: (serviceId: number, lines: number = 100): Promise<{ success: boolean; logs: string }> =>
    apiClient.get(`/mlops/services/${serviceId}/logs?lines=${lines}`),
  registerModel: (data: RegisterModelData): Promise<{ success: boolean; message: string }> =>
    apiClient.post('/mlops/models/register', data),
  deployMLFlow: (projectId: number): Promise<{ success: boolean; mlflowServer: unknown }> =>
    apiClient.post('/mlops/mlflow/deploy', { projectId }),

  ...agentApi,
  ...prebuiltAgentApi
};

export default api;
